import React, { useState } from 'react';
import { Card, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useUpdateProfileMutation, useGetTimezonesQuery } from 'state/slices/profile/profileApiSlice';
import { updateUserInfo } from 'state/slices/auth/authSlice';
import { showSuccessToast } from 'utils/toast';

// Tries to get the device/browser timezone; returns null to let server default if not available
const getSystemTimezone = () => {
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) {
        return timezone;
      }
    } catch (error) {
      console.warn('Failed to get system timezone:', error);
    }
  }
  // Could not get system timezone, let server handle it
  return null;
};

const TimezoneSettings = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const { data: timezonesData, isLoading: isLoadingTimezones } = useGetTimezonesQuery();

  // Get device timezone once
  const systemTimezone = getSystemTimezone();

  // Choose timezone default: user > system > UTC
  const currentTimezone = userInfo?.timezone || systemTimezone || 'UTC';
  const [selectedTimezone, setSelectedTimezone] = useState(currentTimezone);

  // Available timezones from backend or empty array if not loaded
  const timezones = timezonesData?.timezones || [];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submission if nothing changed
    if (selectedTimezone === currentTimezone) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('timezone', selectedTimezone);

      const updatedUser = await updateProfile(formData).unwrap();

      // Replace user info in Redux with updated
      dispatch(updateUserInfo({
        field: 'replace',
        data: updatedUser,
      }));

      showSuccessToast('Timezone updated successfully');
    } catch (err) {
      // Error toast handled in baseQuery
    }
  };

  // The timezone being shown as "current" in form
  const displayCurrentTimezone = currentTimezone;

  return (
    <Row>
      <Col md={6}>
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0 text-black">Timezone Settings</h5>
          </Card.Header>
          <Card.Body className="card-body p-3">
            <Form onSubmit={handleSubmit} className="d-flex align-items-start column-gap-3">
              <Form.Group className="form-group">
                <Form.Select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  disabled={isLoading || isLoadingTimezones}
                  className="mb-1"
                >
                  {isLoadingTimezones ? (
                    <option>Loading timezones...</option>
                  ) : (
                    timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))
                  )}
                </Form.Select>
                <Form.Text className="text-muted">
                  Current timezone: {displayCurrentTimezone}
                </Form.Text>
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading || selectedTimezone === displayCurrentTimezone}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      className="me-2"
                      style={{ width: '18px', height: '18px' }}
                    />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default TimezoneSettings;
